import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useI18n } from '../../../i18n/useI18n';
import { invalidateKnowledgeDatabaseSummaries } from '../knowledgeQueryInvalidation';
import { useKnowledgeDocumentsQuery } from '../queries/useKnowledgeDocumentsQuery';
import type {
  DocumentPublishState,
  DocumentSortField,
} from '../../../types/knowledge';
import {
  DOCUMENT_PAGE_SIZES,
  type DocumentPageSize,
} from '../../../types/userConfig';
import { useUserConfig } from '../../../userConfig/useUserConfig';
import { useDocumentEmbeddingStreams } from './useDocumentEmbeddingStreams';
import { useDocumentListSearchParams } from './useDocumentListSearchParams';
import { useDocumentSelection } from './useDocumentSelection';
import { useDocumentUploadHandlers } from './useDocumentUploadHandlers';
import { useDocumentBulkActions } from './useDocumentBulkActions';

type UseKnowledgeDocumentsTabOptions = {
  databaseId: string;
  canManageDocuments: boolean;
  documentCount: number;
  publishState: DocumentPublishState;
  enabled: boolean;
};

export function useKnowledgeDocumentsTab({
  databaseId,
  canManageDocuments,
  documentCount,
  publishState,
  enabled,
}: UseKnowledgeDocumentsTabOptions) {
  const { locale, translate } = useI18n();
  const {
    knowledgeDocumentsPageSize,
    setKnowledgeDocumentsPageSize,
    isUserConfigReady,
  } = useUserConfig();
  const queryClient = useQueryClient();

  const {
    documentSort,
    documentPage,
    documentSearchInput,
    sourceFilterValue,
    statusFilterValue,
    listOptions,
    hasActiveDocumentFilters,
    handleDocumentSearchChange,
    handleDocumentSortToggle,
    handleSourceFilterChange,
    handleStatusFilterChange,
    setDocumentPage,
  } = useDocumentListSearchParams(knowledgeDocumentsPageSize, publishState);

  const documentsQuery = useKnowledgeDocumentsQuery({
    databaseId,
    listOptions,
    enabled: enabled && isUserConfigReady,
  });

  const documents = useMemo(
    () => documentsQuery.data ?? [],
    [documentsQuery.data],
  );

  const selectionScopeKey = [
    databaseId,
    publishState,
    documentPage,
    documentSort.column,
    documentSort.direction,
    documentSearchInput,
    sourceFilterValue,
    statusFilterValue,
    knowledgeDocumentsPageSize,
  ].join(':');

  const selection = useDocumentSelection({
    documents,
    canManageDocuments,
    selectionScopeKey,
  });

  const upload = useDocumentUploadHandlers({
    databaseId,
    canManageDocuments,
    listOptions,
  });

  const bulkActions = useDocumentBulkActions({
    databaseId,
    canManageDocuments,
    listOptions,
    clearSelection: selection.clearSelection,
    bulkConfirm: selection.bulkConfirm,
    setBulkConfirm: selection.setBulkConfirm,
  });

  const embeddingDocumentIds = useMemo(() => {
    if (publishState !== 'PUBLISHED') {
      return [];
    }

    return (documentsQuery.data ?? [])
      .filter((document) => document.status === 'EMBEDDING')
      .map((document) => document.id);
  }, [documentsQuery.data, publishState]);
  const { embeddingProgressByDocumentId } = useDocumentEmbeddingStreams({
    databaseId,
    documentIds: embeddingDocumentIds,
    enabled: enabled && publishState === 'PUBLISHED',
  });

  const handleManualRefresh = () => {
    void documentsQuery.refetch();
    invalidateKnowledgeDatabaseSummaries(queryClient, databaseId);
  };

  const handleDocumentPageSizeChange = (value: string) => {
    const parsedValue = Number(value);
    if (!DOCUMENT_PAGE_SIZES.some((size) => size === parsedValue)) {
      return;
    }

    setKnowledgeDocumentsPageSize(parsedValue as DocumentPageSize);
    setDocumentPage(1);
  };

  const totalDocumentPages = useMemo(() => {
    if (hasActiveDocumentFilters) {
      return null;
    }

    return Math.max(1, Math.ceil(documentCount / knowledgeDocumentsPageSize));
  }, [documentCount, hasActiveDocumentFilters, knowledgeDocumentsPageSize]);

  const hasPreviousDocumentPage = documentPage > 1;
  const hasNextDocumentPage =
    totalDocumentPages != null
      ? documentPage < totalDocumentPages
      : documents.length === knowledgeDocumentsPageSize;
  const canJumpToLastDocumentPage =
    totalDocumentPages != null && documentPage < totalDocumentPages;

  const handleDocumentSortToggleForColumn = (column: DocumentSortField) => {
    handleDocumentSortToggle(column);
  };

  return {
    locale,
    translate,
    canManageDocuments,
    databaseId,
    publishState,
    fileInputRef: upload.fileInputRef,
    folderInputRef: upload.folderInputRef,
    documentsQuery,
    documents,
    embeddingProgressByDocumentId,
    documentSort,
    documentPage,
    documentSearchInput,
    sourceFilterValue,
    statusFilterValue,
    hasActiveDocumentFilters,
    handleDocumentSearchChange,
    handleDocumentSortToggle: handleDocumentSortToggleForColumn,
    handleSourceFilterChange,
    handleStatusFilterChange,
    setDocumentPage,
    knowledgeDocumentsPageSize,
    handleUpload: upload.handleUpload,
    handleFolderUpload: upload.handleFolderUpload,
    uploadFiles: upload.uploadFiles,
    isUploading: upload.isUploading,
    uploadProgress: upload.uploadProgress,
    isUploadDragOver: upload.isUploadDragOver,
    handleUploadDragEnter: upload.handleUploadDragEnter,
    handleUploadDragLeave: upload.handleUploadDragLeave,
    handleUploadDragOver: upload.handleUploadDragOver,
    handleUploadDrop: upload.handleUploadDrop,
    openFilePicker: upload.openFilePicker,
    openFolderPicker: upload.openFolderPicker,
    handleDownload: bulkActions.handleDownload,
    handleDownloadDocuments: bulkActions.handleDownloadDocuments,
    handleResumeAbandonedDocument: bulkActions.handleResumeAbandonedDocument,
    handleResumeAbandonedDocuments: bulkActions.handleResumeAbandonedDocuments,
    requestRetryDocuments: bulkActions.requestRetryDocuments,
    requestDeleteDocuments: bulkActions.requestDeleteDocuments,
    handleManualRefresh,
    handleDocumentPageSizeChange,
    totalDocumentPages,
    hasPreviousDocumentPage,
    hasNextDocumentPage,
    canJumpToLastDocumentPage,
    pendingDelete: bulkActions.pendingDelete,
    setPendingDelete: bulkActions.setPendingDelete,
    confirmDeleteDocument: bulkActions.confirmDeleteDocument,
    selectedDocumentIds: selection.selectedDocumentIds,
    selectedDocuments: selection.selectedDocuments,
    hasSelection: selection.hasSelection,
    allVisibleSelected: selection.allVisibleSelected,
    someVisibleSelected: selection.someVisibleSelected,
    canBulkDownload: selection.canBulkDownload,
    canBulkDelete: selection.canBulkDelete,
    canBulkResume: selection.canBulkResume,
    canBulkRetry: selection.canBulkRetry,
    toggleDocumentSelected: selection.toggleDocumentSelected,
    toggleSelectAllVisible: selection.toggleSelectAllVisible,
    clearSelection: selection.clearSelection,
    isBulkActionRunning: bulkActions.isBulkActionRunning,
    bulkConfirm: selection.bulkConfirm,
    setBulkConfirm: selection.setBulkConfirm,
    confirmBulkDeleteDocuments: bulkActions.confirmBulkDeleteDocuments,
    confirmBulkRetryDocuments: bulkActions.confirmBulkRetryDocuments,
    downloadBulkRetryDocuments: bulkActions.downloadBulkRetryDocuments,
    isDeletingDocument: bulkActions.isDeletingDocument,
    bulkDeleteProgress: bulkActions.bulkDeleteProgress,
    isRetryDownloading: bulkActions.isRetryDownloading,
    isRetryConfirming: bulkActions.isRetryConfirming,
    isRetryConfirmDisabled: bulkActions.isRetryConfirmDisabled,
    bulkRetryDownloadProgress: bulkActions.bulkRetryDownloadProgress,
    bulkRetryProgress: bulkActions.bulkRetryProgress,
    allRetryFilesPrepared: bulkActions.allRetryFilesPrepared,
  };
}
